import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { Spinner } from "../components/Logo";
import { PageHeader, LevelChip, EmptyState } from "../components/ui";
import { Flag, CheckCircle2, Globe, Lock, Search, Cpu, Fingerprint, Eye } from "lucide-react";

const catIcons = { Web: Globe, Cryptography: Lock, Forensics: Fingerprint, OSINT: Eye, Network: Cpu };

export default function CTF() {
  const [data, setData] = useState(null);
  const [category, setCategory] = useState("");

  useEffect(() => {
    const params = category ? { category } : {};
    api.get("/ctf/challenges", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [category]);

  return (
    <div data-testid="ctf-page">
      <PageHeader overline="CTF Arena" title="Capture The Flag" desc="Sandboxed, hands-on security challenges. Find the flag, submit it, earn XP. Play safe — nothing here touches real systems." />

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setCategory("")} className={`chip px-3 py-1.5 ${!category ? "bg-[#F26522] text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`} data-testid="ctf-cat-all">All</button>
        {data?.categories?.map((c) => (
          <button key={c} onClick={() => setCategory(c)} className={`chip px-3 py-1.5 ${category === c ? "bg-[#F26522] text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`} data-testid={`ctf-cat-${c}`}>{c}</button>
        ))}
      </div>

      {!data ? <Spinner /> : data.challenges.length === 0 ? (
        <EmptyState icon={Flag} title="No challenges here yet" desc="Check back soon or pick another category." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.challenges.map((c) => {
            const Icon = catIcons[c.category] || Flag;
            return (
              <Link key={c.id} to={`/app/ctf/${c.id}`} className="card-hover p-6 scanlines group" data-testid={`ctf-card-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div className="h-11 w-11 rounded-lg bg-[#0B1120] border border-white/10 flex items-center justify-center">
                    <Icon size={20} className="text-[#38bdf8]" />
                  </div>
                  {c.solved && <span className="chip bg-emerald-500/15 text-emerald-400"><CheckCircle2 size={13} /> Solved</span>}
                </div>
                <h3 className="font-heading font-semibold text-lg mt-4 group-hover:text-[#F26522] transition-colors">{c.title}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <span className="chip bg-white/5 text-slate-300">{c.category}</span>
                  <LevelChip level={c.level} />
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                  <span className="font-mono text-[#F26522] text-sm">+{c.xp_reward} XP</span>
                  <Search size={14} className="text-slate-500" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
